/*
 * FROSTBEULE – product page + featured product (owner: PRODUCT). Vanilla JS, loaded with defer.
 * Custom elements (survive shopify:section:load/unload without extra code):
 *   <fb-product>           variant JSON → hidden id input, price (Omnibus rule), labels, availability,
 *                          quantity rules, delivery state, ?variant= URL, gallery media, sticky thumb,
 *                          one-line button labels (short label when the full one does not fit),
 *                          add-to-cart feedback (check state, live region, German fallback error),
 *                          „Benachrichtigen“ link to the newsletter (snow: declarative [data-fb-snow], CORE)
 *   <fb-gallery>           scroll-snap slider on every viewport: swipe, arrows, thumbnails, counter,
 *                          opens <fb-lightbox>, roving tabindex for the zoom buttons (←/→ between images)
 *   <fb-quantity>          − / + stepper for input[name="quantity"] with a small number tick
 *   <fb-sticky-atc>        sticky add-to-cart bar (IntersectionObserver on the main button); while visible
 *                          it publishes its height on <html> (--fb-sticky-atc-h at the bottom,
 *                          --fb-sticky-atc-top-h at the top) and keeps keyboard focus clear of itself
 *   <fb-delivery-estimate> „Lieferung voraussichtlich Mi., 22. Okt. – Fr., 24. Okt.“: dispatch day first
 *                          (Europe/Berlin, working days, nationwide German holidays, cut-off hour),
 *                          then + min…max working days
 * Add to cart itself is Dawn's <product-form> (assets/product-form.js).
 */
(function () {
  'use strict';

  var C = window.FROSTBEULE || {};
  var doc = document;
  var root = doc.documentElement;

  function motion() {
    var fn = (window.FROSTBEULE || C).motion;
    return typeof fn === 'function' ? fn() : root.getAttribute('data-motion') || 'full';
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function formatPrice(cents) {
    var fmt = (window.FROSTBEULE || C).formatMoney;
    if (typeof fmt === 'function') return fmt(cents);
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  /* restart a CSS animation class (keyframes) */
  function pulse(el, cls, ms) {
    if (!el || motion() === 'none') return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
    clearTimeout(el['_fb' + cls]);
    el['_fb' + cls] = setTimeout(function () {
      el.classList.remove(cls);
    }, ms || 400);
  }

  /* ------------------------------------------------------------------------ */
  /* One-line button labels                                                    */
  /* ------------------------------------------------------------------------ */
  /* The label never wraps (CSS). When the full label does not fit (very narrow phones, long
     custom labels), the short label (data-label-short) is shown – only for the buy states
     „preorder“ / „regular“, never for „Ausverkauft“ / „Nicht verfügbar“. */
  function fitLabel(btn) {
    var span = btn && btn.querySelector('[data-fb-label]');
    if (!span) return;
    if (btn._fbLabel == null) btn._fbLabel = span.textContent;
    if (span.textContent !== btn._fbLabel) span.textContent = btn._fbLabel;
    var short = btn.getAttribute('data-label-short');
    var state = btn.getAttribute('data-state');
    if (!short || (state !== 'preorder' && state !== 'regular')) return;
    if (span.clientWidth > 0 && span.scrollWidth > span.clientWidth + 1) span.textContent = short;
  }

  function setLabel(btn, text, state) {
    if (state) btn.setAttribute('data-state', state);
    if (text) btn._fbLabel = text;
    fitLabel(btn);
  }

  /* ------------------------------------------------------------------------ */
  /* <fb-product>                                                              */
  /* ------------------------------------------------------------------------ */
  class FbProduct extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      var json = this.querySelector('script[data-fb-product-json]');
      this.data = null;
      try {
        this.data = json ? JSON.parse(json.textContent) : null;
      } catch (e) {
        this.data = null;
      }

      this.onChange = this.onChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit, true);
      doc.addEventListener('shopify:block:select', this.onBlockSelect);

      this.setupLabels();
      this.setupNotify();
      this.setupBuyFeedback();
      if (this.data && this.data.variants && this.data.variants.length > 1) {
        this.updateOptionStates(this.selectedOptions());
      }
    }

    disconnectedCallback() {
      this._bound = false;
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit, true);
      doc.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.labelObserver) this.labelObserver.disconnect();
      if (this.buyObserver) this.buyObserver.disconnect();
      clearTimeout(this.addedTimer);
      clearTimeout(this.checkTimer);
      this.labelObserver = null;
      this.buyObserver = null;
    }

    setupLabels() {
      var buttons = toArray(this.querySelectorAll('[data-fb-submit]'));
      if (!buttons.length) return;
      var fitAll = function () {
        buttons.forEach(fitLabel);
      };
      fitAll();
      if ('ResizeObserver' in window) {
        var raf = null;
        this.labelObserver = new ResizeObserver(function () {
          if (raf) return;
          raf = requestAnimationFrame(function () {
            raf = null;
            fitAll();
          });
        });
        buttons.forEach(function (btn) {
          this.labelObserver.observe(btn);
        }, this);
      }
      /* web fonts change the text width once they arrive */
      if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(fitAll);
    }

    /* „Benachrichtige mich“ only when a newsletter sign-up is on the page: the Flocken-Post section
       (anchor setting = section id) or, e.g. on the product page, the footer newsletter (no id of
       its own – the link targets its heading via aria-labelledby). */
    setupNotify() {
      var target =
        doc.getElementById('flocken-post') ||
        doc.querySelector('section.fb-guest[id], .fb-guest-list[id]') ||
        doc.querySelector('.fb-footer__news[id], .fb-footer__news[aria-labelledby]');
      var id = target ? target.id || target.getAttribute('aria-labelledby') : '';
      if (id && !doc.getElementById(id)) id = '';
      toArray(this.querySelectorAll('[data-fb-notify]')).forEach(function (link) {
        link.hidden = !id;
        if (id) link.setAttribute('href', '#' + id);
      });
    }

    /* Add-to-cart feedback around Dawn's product-form: live status, short check state on the
       button(s), German fallback error when the request failed without a Shopify message. */
    setupBuyFeedback() {
      this.mainBtn = this.querySelector('product-form [data-fb-submit]');
      this.status = this.querySelector('[data-fb-buy-status]');
      if (!this.mainBtn || !('MutationObserver' in window)) return;
      var self = this;
      this.buyObserver = new MutationObserver(function () {
        var loading = self.mainBtn.classList.contains('loading');
        if (loading && !self._wasLoading) self.onAddStart();
        if (!loading && self._wasLoading) {
          clearTimeout(self.checkTimer);
          self.checkTimer = setTimeout(function () {
            self.onAddEnd();
          }, 140);
        }
        self._wasLoading = loading;
      });
      this.buyObserver.observe(this.mainBtn, { attributes: true, attributeFilter: ['class'] });
    }

    onSubmit(e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;
      this._pending = true;
    }

    say(key) {
      if (!this.status) return;
      var text = this.status.getAttribute('data-text-' + key) || '';
      this.status.textContent = '';
      var status = this.status;
      requestAnimationFrame(function () {
        status.textContent = text;
      });
    }

    onAddStart() {
      this._pending = true;
      this.say('adding');
      toArray(this.querySelectorAll('[data-fb-submit]')).forEach(function (btn) {
        btn.classList.remove('is-added');
      });
    }

    onAddEnd() {
      if (!this._pending) return;
      this._pending = false;
      var wrapper = this.querySelector('.product-form__error-message-wrapper');
      var hasError = !!(wrapper && !wrapper.hidden);
      if (hasError) {
        this.say('error');
        return;
      }
      var cartUi = doc.querySelector('cart-drawer, cart-notification');
      var opened = doc.querySelector('cart-drawer.active, cart-drawer[open], .cart-notification.active, #cart-notification.active');
      if (cartUi && !opened) {
        /* request failed (network) – Dawn only logs it; tell the visitor */
        if (wrapper) {
          var msg = wrapper.querySelector('.product-form__error-message');
          if (msg) msg.textContent = (this.status && this.status.getAttribute('data-text-error')) || '';
          wrapper.hidden = false;
        }
        this.say('error');
        return;
      }
      this.say('added');
      var buttons = toArray(this.querySelectorAll('[data-fb-submit]'));
      var sticky = doc.querySelectorAll('fb-sticky-atc [data-fb-submit]');
      buttons = buttons.concat(toArray(sticky).filter(function (b) {
        return buttons.indexOf(b) === -1;
      }));
      buttons.forEach(function (btn) {
        btn.classList.add('is-added');
      });
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(function () {
        buttons.forEach(function (btn) {
          btn.classList.remove('is-added');
        });
      }, 1700);
    }

    onBlockSelect(e) {
      if (!this.contains(e.target)) return;
      var details = e.target.matches && e.target.matches('details') ? e.target : e.target.querySelector('details');
      if (details) details.open = true;
    }

    selectedOptions() {
      return toArray(this.querySelectorAll('[data-fb-option-group]')).map(function (group) {
        var checked = group.querySelector('[data-fb-option]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      if (!this.data || !this.data.variants) return null;
      for (var i = 0; i < this.data.variants.length; i++) {
        var v = this.data.variants[i];
        var match = v.options.every(function (o, k) {
          return o === options[k];
        });
        if (match) return v;
      }
      return null;
    }

    onChange(e) {
      var target = e.target;
      if (!target || !target.matches || !target.matches('[data-fb-option]')) return;
      var options = this.selectedOptions();
      var variant = this.findVariant(options);
      this.updateOptionStates(options);
      this.applyVariant(variant);
    }

    updateOptionStates(selected) {
      var variants = (this.data && this.data.variants) || [];
      toArray(this.querySelectorAll('[data-fb-option-group]')).forEach(function (group, gi) {
        toArray(group.querySelectorAll('[data-fb-option]')).forEach(function (input) {
          var candidate = selected.slice();
          candidate[gi] = input.value;
          var ok = variants.some(function (v) {
            return (
              v.available &&
              v.options.every(function (o, k) {
                return o === candidate[k];
              })
            );
          });
          var label = input.nextElementSibling;
          if (!label) return;
          label.toggleAttribute('data-unavailable', !ok);
          var hint = label.querySelector('[data-fb-unavailable-text]');
          if (hint) hint.hidden = ok;
        });
        var current = group.querySelector('[data-fb-option-current]');
        if (current) current.textContent = selected[gi] || '';
      });
    }

    applyVariant(variant) {
      var data = this.data || {};
      var available = !!(variant && variant.available);

      toArray(this.querySelectorAll('form input[name="id"]')).forEach(function (input) {
        if (variant) input.value = variant.id;
        input.disabled = !available;
      });

      toArray(this.querySelectorAll('[data-fb-submit]')).forEach(function (btn) {
        var label;
        var state;
        if (!variant) {
          label = btn.dataset.labelUnavailable;
          state = 'unavailable';
        } else if (!variant.available) {
          label = btn.dataset.labelSoldout;
          state = 'soldout';
        } else if (data.preorder) {
          label = btn.dataset.labelPreorder;
          state = 'preorder';
        } else {
          label = btn.dataset.labelRegular;
          state = 'regular';
        }
        setLabel(btn, label, state);
        if (available) btn.removeAttribute('disabled');
        else btn.setAttribute('disabled', 'disabled');
      });

      toArray(this.querySelectorAll('fb-quantity')).forEach(function (qty) {
        if (variant && variant.quantity_rule && typeof qty.setRule === 'function') {
          qty.setRule(variant.quantity_rule.min, variant.quantity_rule.max, variant.quantity_rule.increment);
        }
        if (typeof qty.setDisabled === 'function') qty.setDisabled(!available);
      });

      toArray(this.querySelectorAll('.product-form__error-message-wrapper')).forEach(function (el) {
        el.hidden = true;
      });

      if (variant) this.updatePrice(variant);
      this.updateDelivery(variant);
      this.updateProductId(variant);

      var sticky = this.querySelector('fb-sticky-atc');
      if (sticky && typeof sticky.setDisabled === 'function') sticky.setDisabled(!available);
      if (sticky && variant && variant.thumb && typeof sticky.setThumb === 'function') sticky.setThumb(variant.thumb);

      if (variant && data.updateUrl && window.history && window.history.replaceState) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (e) {
          /* ignore */
        }
      }

      if (variant && variant.featured_media_id) {
        var gallery = this.querySelector('fb-gallery');
        if (gallery && typeof gallery.showMedia === 'function') gallery.showMedia(variant.featured_media_id);
      }

      this.dispatchEvent(new CustomEvent('fb:variant-change', { bubbles: true, detail: { variant: variant } }));
    }

    updatePrice(variant) {
      var data = this.data || {};
      var showCompare = !!(data.hasLowest && variant.compare_at_price && variant.compare_at_price > variant.price);
      var current = formatPrice(variant.price);

      toArray(this.querySelectorAll('[data-fb-price-current]')).forEach(function (el) {
        if (el.textContent.trim() !== current) {
          el.textContent = current;
          pulse(el, 'is-updating', 360);
        }
      });
      toArray(this.querySelectorAll('[data-fb-price-compare]')).forEach(function (el) {
        el.hidden = !showCompare;
        var value = el.querySelector('[data-fb-price-compare-value]');
        if (value) value.textContent = showCompare ? formatPrice(variant.compare_at_price) : '';
      });
      toArray(this.querySelectorAll('[data-fb-price-omnibus]')).forEach(function (el) {
        el.hidden = !showCompare;
      });
      toArray(this.querySelectorAll('[data-fb-price-label]')).forEach(function (el) {
        el.textContent = showCompare ? data.labels.salePrice : data.labels.price;
      });
    }

    updateDelivery(variant) {
      var data = this.data || {};
      var state = 'instock';
      if (!variant || !variant.available) state = 'soldout';
      else if (data.preorder) state = 'preorder';
      else if (variant.backorder) state = 'backorder';
      toArray(this.querySelectorAll('[data-fb-delivery]')).forEach(function (box) {
        box.setAttribute('data-state', state);
        toArray(box.querySelectorAll('[data-fb-delivery-state]')).forEach(function (el) {
          var states = (el.getAttribute('data-fb-delivery-state') || '').split(/\s+/);
          el.hidden = states.indexOf(state) === -1;
        });
        toArray(box.querySelectorAll('[data-fb-delivery-carrier]')).forEach(function (el) {
          el.hidden = state === 'soldout';
        });
      });
      /* the preorder badge only makes sense while the selected variant can be preordered
         ([data-fb-preorder-alt] = what stands in its place otherwise, e.g. the eyebrow) */
      toArray(this.querySelectorAll('[data-fb-preorder-badge]')).forEach(function (el) {
        el.hidden = state !== 'preorder';
      });
      toArray(this.querySelectorAll('[data-fb-preorder-alt]')).forEach(function (el) {
        el.hidden = state === 'preorder';
      });
    }

    /* GPSR: identifier (EAN, else SKU) of the selected variant */
    updateProductId(variant) {
      toArray(this.querySelectorAll('[data-fb-product-id]')).forEach(function (el) {
        var text = variant && variant.product_id_text;
        el.textContent = text || el.getAttribute('data-empty') || '';
      });
    }
  }

  /* ------------------------------------------------------------------------ */
  /* <fb-gallery>                                                              */
  /* ------------------------------------------------------------------------ */
  class FbGallery extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.track = this.querySelector('[data-fb-track]');
      this.slides = toArray(this.querySelectorAll('[data-fb-slide]'));
      this.thumbs = toArray(this.querySelectorAll('[data-fb-thumb]'));
      this.zooms = toArray(this.querySelectorAll('[data-fb-zoom]'));
      this.prev = this.querySelector('[data-fb-prev]');
      this.next = this.querySelector('[data-fb-next]');
      this.counter = this.querySelector('[data-fb-gallery-current]');
      this.counterOf = toArray(this.querySelectorAll('[data-fb-gallery-of]'));
      /* leading slides that are not media (the 3D box) – not counted, like in the lightbox */
      this.offset = parseInt(this.getAttribute('data-offset'), 10) || 0;
      this.active = 0;
      this.onClick = this.onClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onLightboxClose = this.onLightboxClose.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      doc.addEventListener('fb:lightbox-close', this.onLightboxClose);
      if (this.track) this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.slides[0]) this.slides[0].classList.add('is-active');
      this.updateRoving();
      this.updateArrows();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this._bound = false;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      doc.removeEventListener('fb:lightbox-close', this.onLightboxClose);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.flashTimer) clearTimeout(this.flashTimer);
    }

    isSlider() {
      return !!this.track && this.track.scrollWidth > this.track.clientWidth + 2;
    }

    slideIndexOf(el) {
      var slide = el && el.closest('[data-fb-slide]');
      return slide ? this.slides.indexOf(slide) : -1;
    }

    /* Roving tabindex: exactly one zoom button is in the tab order – the one on the active slide
       (or the closest one before it). ←/→/Home/End move between the images. */
    updateRoving() {
      if (!this.zooms.length) return;
      var self = this;
      var current = null;
      this.zooms.forEach(function (btn) {
        var i = self.slideIndexOf(btn);
        if (i <= self.active && (current === null || i >= self.slideIndexOf(current))) current = btn;
      });
      if (!current) current = this.zooms[0];
      this.zooms.forEach(function (btn) {
        btn.setAttribute('tabindex', btn === current ? '0' : '-1');
      });
    }

    updateArrows() {
      var last = this.slides.length - 1;
      if (this.prev) this.prev.disabled = this.active <= 0;
      if (this.next) this.next.disabled = this.active >= last;
      if (this.counter) {
        var n = this.active - this.offset;
        this.counter.textContent = n < 0 ? '3D' : String(n + 1);
        this.counterOf.forEach(function (el) {
          el.hidden = n < 0;
        });
      }
    }

    /* the lightbox closed: show the image that was last seen there and return focus to its zoom button */
    onLightboxClose(e) {
      var id = this.getAttribute('data-lightbox');
      if (!id || !e.target || e.target.id !== id) return;
      var detail = e.detail || {};
      var index = typeof detail.index === 'number' ? detail.index : -1;
      if (index < 0) return;
      var btn = null;
      this.zooms.forEach(function (z) {
        if (parseInt(z.getAttribute('data-lightbox-index'), 10) === index) btn = z;
      });
      if (!btn) return;
      var slide = this.slideIndexOf(btn);
      if (slide >= 0 && slide !== this.active) this.goTo(slide);
      this.zooms.forEach(function (z) {
        z.setAttribute('tabindex', z === btn ? '0' : '-1');
      });
      if (!detail.trigger || this.contains(detail.trigger)) detail.trigger = btn;
    }

    onKeyDown(e) {
      var btn = e.target.closest && e.target.closest('[data-fb-zoom]');
      if (!btn || !this.contains(btn)) return;
      var i = this.zooms.indexOf(btn);
      var next = -1;
      if (e.key === 'ArrowRight' || e.key === 'Right' || e.key === 'ArrowDown' || e.key === 'Down') next = i + 1;
      else if (e.key === 'ArrowLeft' || e.key === 'Left' || e.key === 'ArrowUp' || e.key === 'Up') next = i - 1;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = this.zooms.length - 1;
      else return;
      e.preventDefault();
      next = Math.max(0, Math.min(this.zooms.length - 1, next));
      var target = this.zooms[next];
      if (!target || target === btn) return;
      var index = this.slideIndexOf(target);
      if (index >= 0) this.goTo(index);
      this.zooms.forEach(function (z) {
        z.setAttribute('tabindex', z === target ? '0' : '-1');
      });
      try {
        target.focus({ preventScroll: true });
      } catch (err) {
        target.focus();
      }
    }

    onClick(e) {
      if (e.target.closest('[data-fb-prev]')) {
        this.goTo(this.active - 1);
        return;
      }
      if (e.target.closest('[data-fb-next]')) {
        this.goTo(this.active + 1);
        return;
      }
      var thumb = e.target.closest('[data-fb-thumb]');
      if (thumb && this.contains(thumb)) {
        this.goTo(parseInt(thumb.getAttribute('data-index'), 10) || 0);
        return;
      }
      var zoom = e.target.closest('[data-fb-zoom]');
      if (zoom && this.contains(zoom)) {
        this.openLightbox(parseInt(zoom.getAttribute('data-lightbox-index'), 10) || 0, zoom);
      }
    }

    openLightbox(index, trigger) {
      var id = this.getAttribute('data-lightbox');
      if (!id) return;
      var run = function () {
        var lb = doc.getElementById(id);
        if (lb && typeof lb.open === 'function') lb.open(index, trigger);
      };
      if (customElements.get('fb-lightbox')) run();
      else customElements.whenDefined('fb-lightbox').then(run);
    }

    goTo(index) {
      index = Math.max(0, Math.min(this.slides.length - 1, index));
      var slide = this.slides[index];
      if (!slide || !this.track) return;
      if (this.isSlider()) {
        var left = slide.offsetLeft - this.track.offsetLeft;
        if (slide.offsetParent === this.track) left = slide.offsetLeft;
        if (motion() === 'none') this.track.scrollLeft = left;
        else this.track.scrollTo({ left: left, behavior: 'smooth' });
      }
      this.setActive(index);
    }

    showMedia(mediaId) {
      var index = -1;
      this.slides.forEach(function (slide, i) {
        if (String(slide.getAttribute('data-media-id')) === String(mediaId)) index = i;
      });
      if (index < 0) return;
      this.goTo(index);
      var slide = this.slides[index];
      if (motion() !== 'none' && slide) {
        var self = this;
        this.slides.forEach(function (s) {
          s.classList.remove('is-flash');
        });
        void slide.offsetWidth;
        slide.classList.add('is-flash');
        if (this.flashTimer) clearTimeout(this.flashTimer);
        this.flashTimer = setTimeout(function () {
          slide.classList.remove('is-flash');
          self.flashTimer = null;
        }, 1000);
      }
    }

    onScroll() {
      if (this.raf) return;
      var self = this;
      this.raf = requestAnimationFrame(function () {
        self.raf = null;
        var w = self.slides.length > 1 ? self.slides[1].offsetLeft - self.slides[0].offsetLeft : self.track.clientWidth;
        var index = Math.round(self.track.scrollLeft / (w || 1));
        index = Math.max(0, Math.min(self.slides.length - 1, index));
        if (index !== self.active) self.setActive(index);
      });
    }

    setActive(index) {
      if (index === this.active) return;
      this.active = index;
      this.slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
      this.thumbs.forEach(function (thumb, i) {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      var thumb = this.thumbs[index];
      if (thumb && thumb.parentElement && thumb.parentElement.scrollWidth > thumb.parentElement.clientWidth) {
        var row = thumb.parentElement;
        var target = thumb.offsetLeft - (row.clientWidth - thumb.offsetWidth) / 2;
        row.scrollTo({ left: Math.max(0, target), behavior: motion() === 'none' ? 'auto' : 'smooth' });
      }
      this.updateArrows();
      /* keep keyboard focus where it is when the user is tabbing through the zoom buttons */
      var focused = doc.activeElement;
      if (!(focused && this.zooms.indexOf(focused) !== -1)) this.updateRoving();
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
    }
  }

  /* ------------------------------------------------------------------------ */
  /* <fb-quantity>                                                             */
  /* ------------------------------------------------------------------------ */
  class FbQuantity extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.input = this.querySelector('input');
      this.down = this.querySelector('[data-step="down"]');
      this.up = this.querySelector('[data-step="up"]');
      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.addEventListener('click', this.onClick);
      if (this.input) this.input.addEventListener('change', this.onInput);
      this.sync();
    }

    disconnectedCallback() {
      this._bound = false;
      this.removeEventListener('click', this.onClick);
      if (this.input) this.input.removeEventListener('change', this.onInput);
    }

    isDisabled() {
      return this.hasAttribute('data-disabled');
    }

    setDisabled(disabled) {
      this.toggleAttribute('data-disabled', !!disabled);
      if (this.input) this.input.disabled = !!disabled;
      this.sync();
    }

    setRule(min, max, step) {
      if (!this.input) return;
      var mn = parseInt(min, 10);
      var mx = parseInt(max, 10);
      var st = parseInt(step, 10);
      this.input.min = isNaN(mn) || mn < 1 ? 1 : mn;
      if (isNaN(mx)) this.input.removeAttribute('max');
      else this.input.max = mx;
      this.input.step = isNaN(st) || st < 1 ? 1 : st;
      this.input.value = this.clamp(parseInt(this.input.value, 10));
      this.sync();
    }

    limits() {
      var min = parseInt(this.input.min, 10);
      var max = parseInt(this.input.max, 10);
      var step = parseInt(this.input.step, 10);
      return {
        min: isNaN(min) || min < 1 ? 1 : min,
        max: isNaN(max) ? Infinity : max,
        step: isNaN(step) || step < 1 ? 1 : step,
      };
    }

    clamp(value) {
      var l = this.limits();
      if (isNaN(value)) value = l.min;
      value = Math.max(l.min, Math.min(l.max, value));
      var offset = (value - l.min) % l.step;
      if (offset) value -= offset;
      return value;
    }

    onClick(e) {
      var btn = e.target.closest('button[data-step]');
      if (!btn || !this.input || this.isDisabled()) return;
      e.preventDefault();
      var l = this.limits();
      var before = parseInt(this.input.value, 10) || l.min;
      var up = btn.getAttribute('data-step') === 'up';
      var value = this.clamp(before + (up ? l.step : -l.step));
      this.input.value = value;
      if (value !== before) pulse(this.input, up ? 'is-tick-up' : 'is-tick-down', 260);
      this.sync();
    }

    onInput() {
      this.input.value = this.clamp(parseInt(this.input.value, 10));
      this.sync();
    }

    sync() {
      if (!this.input) return;
      if (this.isDisabled()) {
        if (this.down) this.down.disabled = true;
        if (this.up) this.up.disabled = true;
        return;
      }
      var l = this.limits();
      var value = parseInt(this.input.value, 10) || l.min;
      if (this.down) this.down.disabled = value <= l.min;
      if (this.up) this.up.disabled = value >= l.max;
    }
  }

  /* ------------------------------------------------------------------------ */
  /* <fb-sticky-atc>                                                           */
  /* ------------------------------------------------------------------------ */
  var DESKTOP = '(min-width: 990px)';

  class FbStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.target = doc.getElementById(this.getAttribute('data-target'));
      this.btn = this.querySelector('[data-fb-sticky-submit]');
      this.pastButton = false;
      this.footerVisible = false;
      this.disabled = !!(this.btn && this.btn.disabled);
      if (!this.target || !('IntersectionObserver' in window)) return;

      var self = this;
      this.observeButton();
      /* the sticky header changes height (mobile ↔ desktop, font load): observe again */
      this.headerEl = this.stickyHeader();
      if (this.headerEl && 'ResizeObserver' in window) {
        this.hro = new ResizeObserver(function () {
          if (self.headerOffset() !== self.ioOffset) self.observeButton();
        });
        this.hro.observe(this.headerEl);
      }

      /* hide while the footer is in view, so the bar never covers it */
      this.footer = doc.querySelector('.shopify-section-group-footer-group, footer');
      if (this.footer) {
        this.footerIo = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self.footerVisible = entry.isIntersecting;
          });
          self.update();
        });
        this.footerIo.observe(this.footer);
      }

      /* mirror the main button's loading state (Dawn adds/removes .loading) */
      this.mo = new MutationObserver(function () {
        var loading = self.target.classList.contains('loading');
        if (self.btn) self.btn.classList.toggle('is-loading', loading);
        if (!loading && self.wasLoading) self.afterSubmit();
        self.wasLoading = loading;
      });
      this.mo.observe(this.target, { attributes: true, attributeFilter: ['class'] });

      this.publish = this.publish.bind(this);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.publish);
        this.ro.observe(this);
      }
      this.mq = window.matchMedia ? window.matchMedia(DESKTOP) : null;
      if (this.mq) {
        if (this.mq.addEventListener) this.mq.addEventListener('change', this.publish);
        else if (this.mq.addListener) this.mq.addListener(this.publish);
      }

      this.onFocusIn = this.onFocusIn.bind(this);
      doc.addEventListener('focusin', this.onFocusIn);
    }

    /* The root box starts below the sticky header and is stretched far below the viewport, so the
       button counts as "intersecting" whenever any part of it is visible below the header (a button
       hidden under the header already counts as passed). A jump (anchor link, fast fling) from
       below the viewport to above it still flips the state. */
    observeButton() {
      var self = this;
      var off = this.headerOffset();
      if (this.io) this.io.disconnect();
      this.ioOffset = off;
      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.pastButton = !entry.isIntersecting;
          });
          self.update();
        },
        { rootMargin: -off + 'px 0px 100000px 0px' }
      );
      this.io.observe(this.target);
    }

    stickyHeader() {
      var bar = doc.querySelector('.fb-header--sticky');
      return bar ? bar.closest('.shopify-section') || bar : null;
    }

    /* height of the header that stays on screen (sticky only; a scrolling header covers nothing) */
    headerOffset() {
      var el = this.headerEl && this.headerEl.isConnected ? this.headerEl : this.stickyHeader();
      if (!el || window.getComputedStyle(el).position !== 'sticky') return 0;
      return Math.round(el.getBoundingClientRect().height);
    }

    disconnectedCallback() {
      this._bound = false;
      if (this.io) this.io.disconnect();
      if (this.hro) this.hro.disconnect();
      this.hro = null;
      this.headerEl = null;
      if (this.footerIo) this.footerIo.disconnect();
      if (this.mo) this.mo.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.mq) {
        if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.publish);
        else if (this.mq.removeListener) this.mq.removeListener(this.publish);
      }
      if (this.onFocusIn) doc.removeEventListener('focusin', this.onFocusIn);
      this.io = null;
      this.footerIo = null;
      this.mo = null;
      this.ro = null;
      this.mq = null;
      this.visible = false;
      this.clearOffsets();
    }

    setDisabled(disabled) {
      this.disabled = disabled;
      this.update();
    }

    setThumb(thumb) {
      var img = this.querySelector('[data-fb-sticky-thumb] img');
      if (!img || !thumb || !thumb.src) return;
      if (thumb.srcset) img.setAttribute('srcset', thumb.srcset);
      img.setAttribute('src', thumb.src);
    }

    isTop() {
      return this.classList.contains('fb-sticky-atc--desktop-top') && !!this.mq && this.mq.matches;
    }

    update() {
      var show = this.pastButton && !this.footerVisible && !this.disabled;
      if (show === this.visible) return;
      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
      this.visible = show;
      this.publish();
    }

    publish() {
      var h = this.visible ? this.offsetHeight : 0;
      if (!h) {
        this.clearOffsets();
        return;
      }
      var top = this.isTop();
      root.style.setProperty('--fb-sticky-atc-h', (top ? 0 : h) + 'px');
      root.style.setProperty('--fb-sticky-atc-top-h', (top ? h : 0) + 'px');
      root.classList.toggle('fb-sticky-atc-on', !top);
      root.classList.toggle('fb-sticky-atc-top-on', top);
    }

    clearOffsets() {
      root.style.setProperty('--fb-sticky-atc-h', '0px');
      root.style.setProperty('--fb-sticky-atc-top-h', '0px');
      root.classList.remove('fb-sticky-atc-on', 'fb-sticky-atc-top-on');
    }

    /* Elements that receive focus underneath the bar are scrolled clear of it (elements in fixed
       layers – header, drawers, dialogs, the bar itself – are left alone). */
    onFocusIn(e) {
      if (!this.visible) return;
      var el = e.target;
      if (!el || el === doc.body || this.contains(el) || !el.getBoundingClientRect) return;
      if (el.closest('dialog, [role="dialog"], [aria-modal="true"], .shopify-section-group-header-group')) return;
      for (var n = el; n && n !== doc.body; n = n.parentElement) {
        if (window.getComputedStyle(n).position === 'fixed') return;
      }
      var self = this;
      requestAnimationFrame(function () {
        if (!self.visible || doc.activeElement !== el) return;
        var bar = self.getBoundingClientRect();
        var r = el.getBoundingClientRect();
        if (!r.width && !r.height) return;
        if (self.isTop()) {
          var overTop = bar.bottom - r.top;
          if (overTop > 0) window.scrollBy(0, -(overTop + 16));
        } else {
          var overBottom = r.bottom - bar.top;
          if (overBottom > 0) window.scrollBy(0, overBottom + 16);
        }
      });
    }

    /* show the error message (rendered next to the main button) */
    afterSubmit() {
      var self = this;
      setTimeout(function () {
        var form = self.target.closest('product-form');
        var error = form && form.querySelector('.product-form__error-message-wrapper');
        if (error && !error.hidden) {
          error.scrollIntoView({ block: 'center', behavior: motion() === 'none' ? 'auto' : 'smooth' });
        }
      }, 180);
    }
  }

  /* ------------------------------------------------------------------------ */
  /* <fb-delivery-estimate>                                                    */
  /* ------------------------------------------------------------------------ */
  var DE_DAYS = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];
  var DE_MONTHS = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];
  /* nationwide public holidays in Germany: fixed dates + Karfreitag, Ostermontag,
     Christi Himmelfahrt, Pfingstmontag (offsets from Easter Sunday) */
  var FIXED_HOLIDAYS = ['1-1', '5-1', '10-3', '12-25', '12-26'];
  var EASTER_OFFSETS = [-2, 1, 39, 50];
  var SHOP_TZ = 'Europe/Berlin';
  var holidayCache = {};

  function easterSunday(year) {
    var a = year % 19;
    var b = Math.floor(year / 100);
    var c = year % 100;
    var d = Math.floor(b / 4);
    var e = b % 4;
    var f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4);
    var k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31);
    var day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day, 12);
  }

  function holidays(year) {
    if (holidayCache[year]) return holidayCache[year];
    var list = FIXED_HOLIDAYS.slice();
    var easter = easterSunday(year);
    EASTER_OFFSETS.forEach(function (offset) {
      var d = new Date(easter.getTime());
      d.setDate(d.getDate() + offset);
      list.push(d.getMonth() + 1 + '-' + d.getDate());
    });
    holidayCache[year] = list;
    return list;
  }

  function isWorkingDay(d) {
    var day = d.getDay();
    if (day === 0 || day === 6) return false;
    return holidays(d.getFullYear()).indexOf(d.getMonth() + 1 + '-' + d.getDate()) === -1;
  }

  function nextWorkingDay(d) {
    var n = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12);
    do {
      n.setDate(n.getDate() + 1);
    } while (!isWorkingDay(n));
    return n;
  }

  function addWorkingDays(from, n) {
    var d = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 12);
    for (var added = 0; added < n; added += 1) d = nextWorkingDay(d);
    return d;
  }

  /* calendar date + hour in the shop's time zone (not the visitor's) */
  function shopNow(now) {
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: SHOP_TZ,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        hourCycle: 'h23',
      }).formatToParts(now);
      var get = function (type) {
        for (var i = 0; i < parts.length; i++) if (parts[i].type === type) return parseInt(parts[i].value, 10);
        return NaN;
      };
      var y = get('year');
      var m = get('month');
      var d = get('day');
      var h = get('hour');
      if (isNaN(y) || isNaN(m) || isNaN(d) || isNaN(h)) throw new Error('parts');
      return { date: new Date(y, m - 1, d, 12), hour: h % 24 };
    } catch (e) {
      return { date: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12), hour: now.getHours() };
    }
  }

  /* dispatch day: today if it is a working day and the order comes in before the cut-off,
     else the next working day; delivery = dispatch + min … max working days */
  function deliveryWindow(now, min, max, cutoff) {
    var local = shopNow(now);
    var dispatch = isWorkingDay(local.date) && local.hour < cutoff ? local.date : nextWorkingDay(local.date);
    return { dispatch: dispatch, start: addWorkingDays(dispatch, min), end: addWorkingDays(dispatch, max) };
  }

  function formatDay(d) {
    var locale = ((window.FROSTBEULE || C).config && (window.FROSTBEULE || C).config.locale) || root.lang || 'de';
    if (/^de/i.test(locale)) return DE_DAYS[d.getDay()] + ', ' + d.getDate() + '. ' + DE_MONTHS[d.getMonth()];
    try {
      return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(d);
    } catch (e) {
      return d.toDateString();
    }
  }

  function formatRange(win) {
    var range = formatDay(win.start);
    if (win.end.getTime() !== win.start.getTime()) range += ' – ' + formatDay(win.end);
    return range;
  }

  class FbDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      var min = parseInt(this.getAttribute('data-min'), 10);
      var max = parseInt(this.getAttribute('data-max'), 10);
      var cutoff = parseInt(this.getAttribute('data-cutoff'), 10);
      if (isNaN(min) || min < 0) min = 2;
      if (isNaN(max) || max < min) max = min;
      if (isNaN(cutoff) || cutoff < 0 || cutoff > 24) cutoff = 14;
      var range = formatRange(deliveryWindow(new Date(), min, max, cutoff));
      var template = this.getAttribute('data-template') || '[range]';
      var target = this.querySelector('[data-fb-delivery-estimate-text]');
      if (target) target.textContent = template.replace('[range]', range);
    }
  }

  /* exposed for tests: customElements.get('fb-delivery-estimate').estimate(new Date('2026-10-20T10:00:00Z'), 2, 4, 14) */
  FbDeliveryEstimate.estimate = function (now, min, max, cutoff) {
    return formatRange(deliveryWindow(now, min, max, cutoff == null ? 14 : cutoff));
  };

  define('fb-product', FbProduct);
  define('fb-gallery', FbGallery);
  define('fb-quantity', FbQuantity);
  define('fb-sticky-atc', FbStickyAtc);
  define('fb-delivery-estimate', FbDeliveryEstimate);
})();
